import React, { useEffect, useState, useCallback } from "react";
import "./FileViewerModal.css";

const API_BASE = `https://mykaddle-api.onrender.com/api/groups`;

// ── Helpers ────────────────────────────────────────────────
const getFileType = (url) => {
  const clean = url.split("?")[0].toLowerCase();
  if (/\.(png|jpe?g|gif|webp|svg|bmp)$/.test(clean)) return "image";
  if (/\.pdf$/.test(clean))                            return "pdf";
  if (/\.(mp4|webm|ogg|mov|avi|mkv|wmv)$/.test(clean)) return "video";
  return "other";
};

const getFileName = (url) => {
  try {
    const decoded = decodeURIComponent(url.split("?")[0]);
    return decoded.split("/").pop();
  } catch {
    return url.split("/").pop();
  }
};

// ── Toast Notification Component ───────────────────────────
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`fvm-toast fvm-toast-${type}`}>
      <span className="fvm-toast-icon">
        {type === 'success' ? '' : type === 'error' ? '' : ''}
      </span>
      <span className="fvm-toast-message">{message}</span>
    </div>
  );
};

// ── Main Component ─────────────────────────────────────────
const FileViewerModal = ({ url, fileId, subjectId, onClose }) => {
  const [imgLoaded,    setImgLoaded]    = useState(false);
  const [imgError,     setImgError]     = useState(false);
  const [downloading,  setDownloading]  = useState(false);
  const [openingTab,   setOpeningTab]   = useState(false);
  // For PDF/image we create a local blob URL so the GCS URL never hits the browser bar
  const [blobUrl,      setBlobUrl]      = useState(null);
  const [blobLoading,  setBlobLoading]  = useState(false);
  const [blobError,    setBlobError]    = useState(false);
  const [toast,        setToast]        = useState(null);

  const type     = getFileType(url);
  const fileName = getFileName(url);

  // Show toast notification
  const showToast = (message, type = 'info') => {
    setToast({ message, type });
  };

  // ── Fetch blob through proxy once on mount (pdf + image) ──
  useEffect(() => {
    if (type !== "pdf" && type !== "image") return;

    let revoked = false;
    const fetchBlob = async () => {
      setBlobLoading(true);
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(
          `${API_BASE}/${subjectId}/files/${fileId}/download`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) throw new Error("Fetch failed");
        const blob = await res.blob();
        if (!revoked) {
          setBlobUrl(URL.createObjectURL(blob));
          setImgLoaded(true);
        }
      } catch (err) {
        console.error("Blob fetch error:", err);
        if (!revoked) setBlobError(true);
      } finally {
        if (!revoked) setBlobLoading(false);
      }
    };

    fetchBlob();

    return () => {
      revoked = true;
      // revoke on unmount to free memory
      setBlobUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null; });
    };
  }, [fileId, subjectId, type]);

  // ── Close on Escape ────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  // ── Lock body scroll ───────────────────────────────────────
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const handleBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  // ── Download via proxy with notification ─────────────────────
  const handleDownload = useCallback(async () => {
    setDownloading(true);
    showToast(`Starting download: ${fileName}`, 'info');
    
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${API_BASE}/${subjectId}/files/${fileId}/download`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error("Download failed");
      
      const blob = await res.blob();
      const tempUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = tempUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(tempUrl);
      
      // Show success message
      showToast(`Download started! ${fileName} will appear in your media folder`, 'success');
    } catch (err) {
      console.error("Download error:", err);
      showToast(`Download failed. Please try again.`, 'error');
    } finally {
      setDownloading(false);
    }
  }, [subjectId, fileId, fileName]);

  // ── Open in new tab via proxy blob ─────────────────────────
  const handleOpenInTab = useCallback(async () => {
    // If we already have the blob URL loaded (pdf/image), reuse it
    if (blobUrl) {
      window.open(blobUrl, "_blank");
      return;
    }
    setOpeningTab(true);
    showToast(`Opening ${fileName} in new tab...`, 'info');
    
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${API_BASE}/${subjectId}/files/${fileId}/download`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error("Fetch failed");
      const blob = await res.blob();
      const tempUrl = URL.createObjectURL(blob);
      window.open(tempUrl, "_blank");
      // Give the tab a moment to receive the blob before revoking
      setTimeout(() => URL.revokeObjectURL(tempUrl), 10000);
    } catch (err) {
      console.error("Open-in-tab error:", err);
      showToast(`Failed to open file`, 'error');
    } finally {
      setOpeningTab(false);
    }
  }, [blobUrl, subjectId, fileId, fileName]);

  // ── Render ─────────────────────────────────────────────────
  return (
    <>
      <div className="fvm-backdrop" onClick={handleBackdrop}>
        <div className="fvm-modal">

          {/* Header */}
          <div className="fvm-header">
            <div className="fvm-file-info">
              <span className="fvm-file-icon">{typeIcon(type)}</span>
              <span className="fvm-file-name" title={fileName}>{fileName}</span>
            </div>
            <div className="fvm-actions">
              <button
                className="fvm-action-btn"
                onClick={handleDownload}
                disabled={downloading}
                title="Download"
              >
                {downloading ? <span className="fvm-btn-spinner" /> : <DownloadIcon />}
              </button>
              <button
                className="fvm-action-btn"
                onClick={handleOpenInTab}
                disabled={openingTab}
                title="Open in new tab"
              >
                {openingTab ? <span className="fvm-btn-spinner" /> : <ExternalIcon />}
              </button>
              <button className="fvm-close-btn" onClick={onClose} title="Close">×</button>
            </div>
          </div>

          {/* Body */}
          <div className="fvm-body">

            {/* IMAGE */}
            {type === "image" && (
              <div className="fvm-img-wrap">
                {blobLoading && <div className="fvm-spinner" />}
                {blobError ? (
                  <div className="fvm-unsupported">
                    <span>⚠️</span>
                    <p>Could not load image.</p>
                    <button className="fvm-open-btn" onClick={handleDownload}>
                      Download instead
                    </button>
                  </div>
                ) : blobUrl ? (
                  <img
                    src={blobUrl}
                    alt={fileName}
                    className="fvm-img loaded"
                  />
                ) : null}
              </div>
            )}

            {/* PDF — iframe uses the local blob URL, not the GCS URL */}
            {type === "pdf" && (
              blobLoading ? (
                <div className="fvm-img-wrap">
                  <div className="fvm-spinner" />
                </div>
              ) : blobError ? (
                <div className="fvm-unsupported">
                  <span>⚠️</span>
                  <p>Could not load PDF.</p>
                  <button className="fvm-open-btn" onClick={handleDownload}>
                    Download instead
                  </button>
                </div>
              ) : blobUrl ? (
                <iframe
                  className="fvm-iframe"
                  src={`${blobUrl}#toolbar=0&navpanes=0`}
                  title={fileName}
                />
              ) : null
            )}

            {/* VIDEO */}
            {type === "video" && (
              <div className="fvm-unsupported">
                <span>🎬</span>
                <p className="fvm-unsupported-title">Video file</p>
                <p>{fileName}</p>
                <button
                  className="fvm-open-btn fvm-download-btn"
                  onClick={handleDownload}
                  disabled={downloading}
                >
                  {downloading
                    ? <><span className="fvm-btn-spinner" /> Downloading…</>
                    : <><DownloadIcon /> Download video</>
                  }
                </button>
                
              </div>
            )}

            {/* OTHER */}
            {type === "other" && (
              <div className="fvm-unsupported">
                <span>📄</span>
                <p>Preview not available for this file type.</p>
                <button
                  className="fvm-open-btn"
                  onClick={handleDownload}
                  disabled={downloading}
                >
                  {downloading ? "Downloading…" : "Download file"}
                </button>
              </div>
            )}

          </div>
        </div>
      </div>

      {/* Toast Notifications */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

// ── Icons ──────────────────────────────────────────────────
const typeIcon = (type) =>
  ({ image: "🖼️", pdf: "📄", video: "🎬", other: "📎" }[type]);

const DownloadIcon = () => (
  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M12 15V3m0 12-4-4m4 4 4-4M2 17l.621 2.485A2 2 0 0 0 4.561 21h14.878a2 2 0 0 0 1.94-1.515L22 17" />
  </svg>
);

const ExternalIcon = () => (
  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14 21 3" />
  </svg>
);

export default FileViewerModal;